import { type FC } from 'react';

import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ResourceSection, {
  ResourceSectionProps,
} from 'layout/Listing/variants/resourceCenter/components/ResourceSection';

import { hasArrayValues } from 'utils/array/arrays';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';

import resources from 'data/resources.json';

import type { ListingConfigProps } from 'layout/Listing/config';

type ResourceItemProps = {
  label: string;
  link: string;
  posts: { data: BlogResource }[];
};

const mergeResourceLists = (primary: ResourceItemProps[], fallback: ResourceItemProps[]): ResourceItemProps[] => {
  const seen = new Set<string>();
  const result: ResourceItemProps[] = [];

  const addItem = (item: ResourceItemProps) => {
    const key = item?.label;
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    result.push(item);
  };

  primary.forEach(addItem);
  fallback.forEach(addItem);

  return result;
};

const toAnchorId = (label: string | undefined): string | undefined =>
  label
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const ResourceCenter: FC<ListingConfigProps> = ({ sectionHeading, filterLabels, ...props }) => {
  const {
    sectionProps,
    rest: { builderState },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<ResourceItemProps>(rawLatestData);
  const localResources = toArray<ResourceItemProps>(resources);

  const resourceList: ResourceItemProps[] = hasArrayValues(latestData)
    ? mergeResourceLists(latestData, localResources)
    : localResources;

  if (!hasArrayValues(resourceList)) {
    console.warn('[ResourceCenter] No items to render – check builderState/listingData and resources.json');
  }

  return (
    <Section {...sectionProps}>
      <div className="flex w-full flex-col gap-16">
        <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-16 px-4">
          {hasArrayValues(resourceList) && (
            <div className="flex flex-col gap-16">
              {sectionHeading && (
                <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 dark:text-white">
                  {sectionHeading}
                </h2>
              )}

              {resourceList.map(resource => {
                const matchedFilter = filterLabels?.find(f => f.heading === resource.label);
                const labelBuilder = matchedFilter?.label || resource.label;

                const rawAnchorId = matchedFilter?.anchorId || toAnchorId(resource.label);
                const anchorId = rawAnchorId?.replace(/^#/, '');

                return (
                  <section key={resource.label} id={anchorId} className="scroll-mt-[120px]">
                    <ResourceSection {...(resource as ResourceSectionProps)} labelBuilder={labelBuilder} />
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default ResourceCenter;
